import { dateToApiFormat, isDateInPast, secondsToTimeString } from '@/helpers/dateHelper';
import {
  FixedLeaveTypeId,
  UNKNOWN_LEAVE_TYPE_LABEL,
  UNKNOWN_LEAVE_TYPE_SURFACE_COLOR
} from '@/services/availability/availability.types';
import { useAvailabilityTypes } from '@/services/availability/useAvailabilityTypes';
import { EmployeeScheduleDay } from '@/services/employee/schedule/schedule.types';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { CSSProperties, FC, ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import strings from '../team.translations';
import { getTeamCalendarLeaveTypeTextClass } from './teamCalendarLeaveTypeStyles';
import { FaAsterisk } from 'react-icons/fa';

interface TeamCalendarCellProps {
  date: Date;
  employeeId: string;
  cell: EmployeeScheduleDay;
  teamCalendarAnchorDate: string;
}

interface TeamCalendarCellMenuItemsProps {
  open: boolean;
  buttonRef: RefObject<HTMLButtonElement | null>;
  onOpenCalendar: () => void;
  onEditSchedule: () => void;
  canEditSchedule: boolean;
}

const MENU_WIDTH = 192;
const MENU_HEIGHT = 96;
const VIEWPORT_PADDING = 8;

const TeamCalendarCellMenuItems: FC<TeamCalendarCellMenuItemsProps> = ({
  open,
  buttonRef,
  onOpenCalendar,
  onEditSchedule,
  canEditSchedule
}) => {
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  useEffect(() => {
    if (!open) {
      return;
    }

    const updateMenuPosition = () => {
      const button = buttonRef.current;

      if (!button) {
        return;
      }

      const rect = button.getBoundingClientRect();
      const centeredLeft = rect.left + rect.width / 2 - MENU_WIDTH / 2;
      const maxLeft = window.innerWidth - MENU_WIDTH - VIEWPORT_PADDING;
      const left = Math.max(VIEWPORT_PADDING, Math.min(centeredLeft, maxLeft));
      const showAbove = rect.bottom + MENU_HEIGHT > window.innerHeight - VIEWPORT_PADDING && rect.top > MENU_HEIGHT;

      setMenuStyle({
        position: 'fixed',
        width: `${MENU_WIDTH}px`,
        left: `${left}px`,
        top: showAbove ? `${rect.top - MENU_HEIGHT}px` : `${rect.bottom}px`
      });
    };

    updateMenuPosition();

    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [buttonRef, open]);

  if (!open) {
    return null;
  }

  return createPortal(
    <MenuItems
      static
      className="z-20 rounded-md border bg-primary-std text-left"
      style={{
        ...menuStyle,
        boxShadow: '0 12px 24px rgba(17, 24, 39, 0.28)'
      }}
    >
      <MenuItem>
        {({ focus }) => (
          <button
            type="button"
            onClick={onOpenCalendar}
            className={`w-full px-5 py-2 text-left text-sm text-primary-std ${focus ? 'bg-secondary-std' : ''}`}
          >
            {strings.teamCalendar.viewDetails}
          </button>
        )}
      </MenuItem>
      <MenuItem disabled={!canEditSchedule}>
        {({ focus, disabled }) =>
          disabled ? (
            <div
              aria-disabled="true"
              title={strings.teamCalendar.editScheduleDisabledReason}
              className="w-full cursor-not-allowed border-t px-5 py-2 text-left text-sm text-gray-400"
            >
              {strings.teamCalendar.editSchedule}
            </div>
          ) : (
            <button
              type="button"
              onClick={onEditSchedule}
              className={`w-full border-t px-5 py-2 text-left text-sm text-primary-std ${focus ? 'bg-secondary-std' : ''}`}
            >
              {strings.teamCalendar.editSchedule}
            </button>
          )
        }
      </MenuItem>
    </MenuItems>,
    document.body
  );
};

export const TeamCalendarCell: FC<TeamCalendarCellProps> = ({ date, employeeId, cell, teamCalendarAnchorDate }) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const navigate = useNavigate();
  const leaveTypes = useAvailabilityTypes();
  const leaveType = cell.leaveType ? leaveTypes.find(type => type.leaveTypeId === cell.leaveType) : undefined;
  const unknownLeaveType = cell.leaveType === FixedLeaveTypeId.UNKNOWN;
  const canEditAvailability = !isDateInPast(date, new Date());

  const hasAvailabilityChange =
    cell.availableTimes.length > 0 &&
    cell.availableTimes.some(time => time.start !== cell.patternStart || time.end !== cell.patternEnd);

  const openTeamMemberCalendar = () => {
    navigate(`/team-member/${employeeId}/calendar-details/${dateToApiFormat(date)}`);
  };

  const openEditSchedule = () => {
    navigate(
      `/team-member/${employeeId}/set-schedule?date=${dateToApiFormat(date)}&from=team-calendar&teamCalendarAnchorDate=${teamCalendarAnchorDate}`
    );
  };

  let content: ReactNode = null;
  let className = 'text-xs text-secondary-std';
  const buttonStyle: CSSProperties = {};

  if (cell.shifts.length > 1) {
    className = 'bg-primary-500 text-white';
    content = <span className="text-3xl font-normal leading-none">{cell.shifts.length}</span>;
  } else if (cell.shifts.length === 1) {
    const shift = cell.shifts[0];

    content = (
      <span className="flex flex-col items-center justify-center text-[11px] font-normal">
        <span>{secondsToTimeString(shift.start)}</span>
        <span>{secondsToTimeString(shift.end)}</span>
      </span>
    );
    className = 'bg-primary-500 text-white';
  } else if (cell.leaveType) {
    const label = unknownLeaveType
      ? UNKNOWN_LEAVE_TYPE_LABEL.charAt(0).toUpperCase()
      : (leaveType?.name.charAt(0).toUpperCase() ?? '?');

    className = unknownLeaveType ? 'bg-gray-200 text-gray-700' : getTeamCalendarLeaveTypeTextClass(leaveType);
    content = <span className="text-xl font-normal leading-none">{label}</span>;
    if (unknownLeaveType) {
      buttonStyle.backgroundColor = UNKNOWN_LEAVE_TYPE_SURFACE_COLOR;
    } else if (leaveType?.color) {
      buttonStyle.backgroundColor = leaveType.color;
    }
  } else if (hasAvailabilityChange) {
    className = 'text-xs text-gray-400';
    content =
      cell.availableTimes.length > 1 ? (
        <span className="text-3xl font-normal leading-none text-gray-500">{cell.availableTimes.length}</span>
      ) : (
        <span className="flex flex-col items-center justify-center text-[11px] font-normal">
          <FaAsterisk className="w-4 h-4" />
        </span>
      );
    if (cell.availableTimes.length > 1) {
      className = 'bg-gray-100 text-xs text-gray-500';
    }
  }

  return (
    <Menu as="div" className="relative h-full w-full">
      {({ open }) => (
        <>
          <MenuButton
            ref={buttonRef}
            type="button"
            className={`flex h-full w-full min-w-0 items-center justify-center p-1 text-center transition ${className}`}
            style={{
              ...buttonStyle,
              ...(open
                ? {
                    boxShadow: 'inset 0 0 0 3px #111827'
                  }
                : {})
            }}
          >
            {content ?? <span className="block h-full w-full" />}
          </MenuButton>
          <TeamCalendarCellMenuItems
            open={open}
            buttonRef={buttonRef}
            onOpenCalendar={openTeamMemberCalendar}
            onEditSchedule={openEditSchedule}
            canEditSchedule={canEditAvailability}
          />
        </>
      )}
    </Menu>
  );
};
